import { AWS_CONFIG } from '../config/aws';

const API = AWS_CONFIG.apiUrl;

// listPosts({ category, cursor }) → { posts, nextCursor }
// 401/404 etc throw with .status property
export async function listPosts({ category, cursor } = {}) {
  const params = new URLSearchParams({ category });
  if (cursor) params.set('cursor', cursor);
  const res = await fetch(`${API}/community/posts?${params.toString()}`);
  if (!res.ok) {
    const err = new Error(`목록 조회 실패 (${res.status})`);
    err.status = res.status;
    throw err;
  }
  return res.json();
}

// getPost(id) → { post }
export async function getPost(id) {
  const res = await fetch(`${API}/community/posts/${encodeURIComponent(id)}`);
  if (!res.ok) {
    const err = new Error(res.status === 404 ? '글을 찾을 수 없습니다' : `조회 실패 (${res.status})`);
    err.status = res.status;
    throw err;
  }
  return res.json();
}

// searchPosts({ q, category }) → { posts }  (search ignores cursor in v1)
export async function searchPosts({ q, category } = {}) {
  const params = new URLSearchParams({ q });
  if (category) params.set('category', category);
  const res = await fetch(`${API}/community/posts/search?${params.toString()}`);
  if (!res.ok) {
    const err = new Error(`검색 실패 (${res.status})`);
    err.status = res.status;
    throw err;
  }
  return res.json();
}

// Category metadata used by tabs and breadcrumbs
export const CATEGORIES = [
  { id: 'review', label: '후기', emoji: '⭐' },
  { id: 'qna',    label: 'Q&A',  emoji: '💬' },
  { id: 'free',   label: '자유', emoji: '🗨️' },
  { id: 'notice', label: '공지', emoji: '📢' },
];

export function categoryLabel(id) {
  return CATEGORIES.find(c => c.id === id)?.label || id;
}

// === Phase 3: Auth-required write endpoints ===

// All write functions require an idToken from AuthContext.getIdToken().
// Caller is responsible for passing a valid token.

function authHeaders(token) {
  return {
    'Content-Type': 'application/json',
    Authorization: token,
  };
}

async function handleAuthResponse(res, defaultErrorMessage) {
  if (!res.ok) {
    let body = {};
    try { body = await res.json(); } catch {}
    const err = new Error(body.error || defaultErrorMessage);
    err.status = res.status;
    throw err;
  }
  return res.json();
}

export async function createPost({ token, category, title, body, imageUrls = [] }) {
  const res = await fetch(`${API}/community/posts`, {
    method: 'POST',
    headers: authHeaders(token),
    body: JSON.stringify({ category, title, body, imageUrls }),
  });
  return handleAuthResponse(res, '글 작성 실패');
}

export async function updatePost({ token, postId, category, title, body, imageUrls = [] }) {
  const res = await fetch(`${API}/community/posts/${encodeURIComponent(postId)}`, {
    method: 'PUT',
    headers: authHeaders(token),
    body: JSON.stringify({ category, title, body, imageUrls }),
  });
  return handleAuthResponse(res, '글 수정 실패');
}

// Phase 4: image upload via presigned POST
// 1) presignUpload(token, contentType) → { url, fields, key, imageUrl }
// 2) uploadToS3(presigned, file) → resolves on 204 with imageUrl
export async function presignUpload({ token, contentType }) {
  const res = await fetch(`${API}/community/uploads/presign`, {
    method: 'POST',
    headers: authHeaders(token),
    body: JSON.stringify({ contentType }),
  });
  return handleAuthResponse(res, '업로드 준비 실패');
}

export async function uploadToS3(presigned, file) {
  const form = new FormData();
  Object.entries(presigned.fields).forEach(([k, v]) => form.append(k, v));
  form.append('file', file);
  const res = await fetch(presigned.url, { method: 'POST', body: form });
  if (!res.ok) {
    const text = await res.text().catch(() => '');
    const err = new Error(`S3 업로드 실패 (${res.status})`);
    err.status = res.status;
    err.detail = text;
    throw err;
  }
  return { imageUrl: presigned.imageUrl, key: presigned.key };
}

// Admin-only. Toggles post.isHidden. Server enforces admin guard.
export async function hidePost({ token, postId, isHidden }) {
  const res = await fetch(`${API}/community/posts/${encodeURIComponent(postId)}/hide`, {
    method: 'PUT',
    headers: authHeaders(token),
    body: JSON.stringify({ isHidden }),
  });
  return handleAuthResponse(res, '글 숨김 변경 실패');
}

// Admin-only listing that includes hidden posts.
export async function listPostsAdmin({ token, category, cursor }) {
  const params = new URLSearchParams({ category, includeHidden: 'true' });
  if (cursor) params.set('cursor', cursor);
  const res = await fetch(`${API}/community/posts?${params.toString()}`, {
    headers: authHeaders(token),
  });
  return handleAuthResponse(res, '관리자 목록 조회 실패');
}

// Admin-only. Toggles comment.isHidden.
export async function hideComment({ token, commentId, isHidden }) {
  const res = await fetch(`${API}/community/comments/${encodeURIComponent(commentId)}/hide`, {
    method: 'PUT',
    headers: authHeaders(token),
    body: JSON.stringify({ isHidden }),
  });
  return handleAuthResponse(res, '댓글 숨김 변경 실패');
}

export async function deletePost({ token, postId }) {
  const res = await fetch(`${API}/community/posts/${encodeURIComponent(postId)}`, {
    method: 'DELETE',
    headers: authHeaders(token),
  });
  return handleAuthResponse(res, '글 삭제 실패');
}

export async function listComments(postId) {
  const res = await fetch(`${API}/community/posts/${encodeURIComponent(postId)}/comments`);
  if (!res.ok) {
    const err = new Error(`댓글 조회 실패 (${res.status})`);
    err.status = res.status;
    throw err;
  }
  return res.json();
}

export async function createComment({ token, postId, body }) {
  const res = await fetch(`${API}/community/posts/${encodeURIComponent(postId)}/comments`, {
    method: 'POST',
    headers: authHeaders(token),
    body: JSON.stringify({ body }),
  });
  return handleAuthResponse(res, '댓글 작성 실패');
}

export async function deleteComment({ token, commentId }) {
  const res = await fetch(`${API}/community/comments/${encodeURIComponent(commentId)}`, {
    method: 'DELETE',
    headers: authHeaders(token),
  });
  return handleAuthResponse(res, '댓글 삭제 실패');
}

export async function getMyNickname(token) {
  const res = await fetch(`${API}/community/nicknames/me`, {
    headers: authHeaders(token),
  });
  return handleAuthResponse(res, '닉네임 조회 실패');
}

export async function updateMyNickname({ token, nickname }) {
  const res = await fetch(`${API}/community/nicknames/me`, {
    method: 'PUT',
    headers: authHeaders(token),
    body: JSON.stringify({ nickname }),
  });
  return handleAuthResponse(res, '닉네임 변경 실패');
}
